<template>
	<header
		:class="[
			warnAboutProd
				? 'border-b-red bg-blue-100 dark:bg-gray-700'
				: 'border-b-orange bg-white dark:bg-gray-500 ',
			isMirusUser ? 'pr-0' : 'pr-6',
		]"
		class="sticky top-0 z-50 flex h-20 w-screen items-center justify-between gap-x-2 border-b-2 pl-6 print:hidden"
	>
		<slot name="skipLink" />

		<div class="flex items-center">
			<button
				v-if="$route.path !== '/'"
				class="mr-4 focus:ring focus:ring-orange focus:ring-offset-2"
				:aria-expanded="`${drawerIsOpen}`"
				:aria-label="`${drawerIsOpen ? 'Hide' : 'Show'} navigation drawer`"
				@click="drawerIsOpen = !drawerIsOpen"
				@keydown.enter.prevent="handleKeyboardSideNavToggle()"
			>
				<FAIcon class="text-3xl text-gray-700 dark:text-gray-300" icon="bars" />
			</button>

			<AppLink :to="mainLogoLink" class="!no-underline" exact>
				<div
					class="flex flex-col items-center text-sm font-bold leading-none text-gray-700 dark:text-gray-400 sm:flex-row sm:gap-x-1 md:text-2xl xl:gap-x-2 xl:text-4xl"
				>
					<div class="">my</div>
					<span
						class="inline-flex h-6 w-6 items-center justify-center rounded-full border-2 border-gray-700 bg-gray-100 shadow-xl dark:bg-orange-600 lg:h-10 lg:w-10 lg:border-4 xl:h-16 xl:w-16"
					>
						<span
							class="md:text-md text-sm font-extrabold leading-none text-orange-600 dark:text-gray-100 xl:text-4xl"
						>
							SF
						</span>
					</span>
					<div class="">domain</div>
					<span v-if="overview" class="uppercase tracking-widest">overview</span>
				</div>
			</AppLink>
		</div>

		<p
			v-if="devMode"
			class="!mt-0 flex-1 truncate text-center text-xs uppercase tracking-widest text-orange-400"
		>
			dev mode dev mode dev mode dev mode dev mode dev <br />
			dev mode dev mode dev mode dev mode dev mode dev <br />
			dev mode dev mode dev mode dev mode dev mode dev <br />
			dev mode dev mode dev mode dev mode dev mode dev <br />
		</p>

		<MirusOnlyBorder dense inline>
			<UseDark v-slot="{ isDark, toggleDark }" attribute="color-scheme" class="shrink-0">
				<BaseToggle :value="isDark" @update:value="toggleDark"> Dark mode </BaseToggle>
			</UseDark>
			<AppLink
				:href="`https://gitlab.mirus.io/root/statefarm/mysfdomain-ui/-/commit/${versionNumber}`"
				class="w-full whitespace-nowrap p-1 text-center text-sm"
			>
				Current commit SHA: {{ devMode ? 'DEV MODE' : versionNumber }}
			</AppLink>
		</MirusOnlyBorder>

		<div class="flex items-center gap-x-2">
			<div class="flex flex-col items-end">
				<AppLink :show-icon="false" class="space-x-2" color="gray" href="tel:18778283131">
					<span class="font-bold md:hidden">Call</span>
					<span class="font-bold max-md:hidden">1-877-828-3131</span>
					<FAIcon class="max-sm:hidden" icon="phone" />
				</AppLink>

				<AppLink
					:show-icon="false"
					class="space-x-2"
					color="gray"
					href="mailto:support@mysfdomain.com"
				>
					<span class="font-bold">Email <span class="max-sm:hidden">Support</span></span>
					<FAIcon class="max-sm:hidden" icon="envelope" />
				</AppLink>
			</div>
			<MirusOnlyBorder dense inline>
				<template #fallback>
					<div
						class="hidden h-12 w-12 shrink-0 rounded-full border-gray-600 bg-white shadow-lg md:inline"
					>
						<img
							:src="mirusLogo"
							alt="Mirus Research loves domains"
							class="aspect-square w-full rounded-full bg-orange-500 p-1"
						/>
					</div>
				</template>
				<div class="flex grow-0 items-center gap-x-2 rounded-l-md">
					<div class="flex items-center justify-end gap-x-2">
						<BaseSelect
							v-if="devMode"
							id="host-target"
							:value.sync="hostTarget"
							pill
							outlined
							class="hidden lg:block"
							:items="['mysfdomain.local', 'mysfdomain']"
							label="backend host"
							@update:value="reload"
						/>

						<div class="flex grow-0 flex-col space-y-1">
							<ViewAsUser />
						</div>
						<button
							class="group h-12 w-12 shrink-0 rounded-full border-4 border-solid border-gray-600 bg-white shadow-lg transition-colors hover:border-gray-500"
							@click="$emit('update:mirusControls', !mirusControls)"
						>
							<img
								:src="mirusLogo"
								alt="Mirus Research loves domains"
								class="h-full w-full rounded-full bg-orange-600 transition-colors group-hover:bg-orange-700"
							/>
						</button>
					</div>
				</div>
			</MirusOnlyBorder>
		</div>
	</header>
</template>
<script setup>
import { computed, nextTick } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute } from 'vue-router/composables';
import { UseDark } from '@vueuse/components';

import mirusLogo from '@/assets/images/editor/logo/mirusControls.svg';

import useSupportStore from '@/stores/support';
import useUserStore from '@/stores/user';
import useAppVersionStore from '@/stores/appVersion';
import useNavigationStore from '@/stores/navigation';

import BaseToggle from '@/components/ui/BaseToggle.vue';
import BaseSelect from '@/components/ui/BaseSelect';
import AppLink from '@/components/ui/AppLink.vue';
import MirusOnlyBorder from '@/components/common/MirusOnlyBorder';

import ViewAsUser from '@/components/ViewAsUser';

defineProps({
	mirusControls: { type: Boolean, default: false },
});

const route = useRoute();

const userStore = useUserStore();
const { isMirusUser } = storeToRefs(userStore);

const supportStore = useSupportStore();
const { hostTarget, warnAboutProd, devMode } = storeToRefs(supportStore);

const appVersionStore = useAppVersionStore();
const { versionNumber } = storeToRefs(appVersionStore);

const navigationStore = useNavigationStore();
const { drawerIsOpen, navSkipLink } = storeToRefs(navigationStore);

const overview = computed(() => route.path.match('/overview'));

const mainLogoLink = computed(() => {
	if (overview.value) {
		return { name: 'overview' };
	}

	if (route.params?.agentID) {
		return {
			name: 'dashboard',
			params: {
				agentID: route.params?.agentID,
			},
		};
	}

	return { name: 'index' };
});

function reload() {
	window.location.reload();
}
async function handleKeyboardSideNavToggle() {
	drawerIsOpen.value = !drawerIsOpen.value;
	if (drawerIsOpen.value) {
		await nextTick();
		navSkipLink.value?.focus();
	}
}
</script>
